import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { CrmService, TransporterFleetOption } from '../../crm/crm.service';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { EntityStatusService } from '../../services/entity-status.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { isSisterCompanyResource, resolveSisterCompanyScope, sisterCompanyFleetAccessMessage, SisterCompanyScope } from '../../shared/utils/sister-company-scope.util';
import { Vehicle } from './vehicle.model';
import { VehicleService } from './vehicle.service';
import { FleetResourceSource, QuickFleetOnboardingDialogComponent } from '../quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { FleetOwnershipTransferDialogComponent, OwnershipTransferResource } from '../ownership-transfer/fleet-ownership-transfer-dialog.component';

@Component({
    selector: 'app-vehicle',
    templateUrl: './vehicle.component.html',
    styleUrls: ['./vehicle.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        BreadcrumbModule,
        DialogModule,
        ConfirmDialogModule,
        TabsModule,
        TagModule,
        ProgressBarModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        ChipModule,
        DatePickerModule,
        SelectModule,
        SelectButtonModule,
        SplitButtonModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        DashboardCompanyScopeSelectorComponent,
        QuickFleetOnboardingDialogComponent,
        FleetOwnershipTransferDialogComponent
    ]
})
export class VehicleComponent implements OnInit {
    private readonly tabKeys = ['owned', 'freelancer', 'rental', 'transporter'] as const;
    readonly resourceViewOptions = [
        { label: 'Current Company', value: 'current' },
        { label: 'Sister Companies', value: 'sister' }
    ];

    isLoading = false;
    quickFleetOnboardingVisible = false;
    quickFleetOnboardingSource: FleetResourceSource = 'owned';
    ownershipTransferVisible = false;
    ownershipTransferResources: OwnershipTransferResource[] = [];
    ownershipTransferPairedResource: OwnershipTransferResource | null = null;
    readonly createVehicleItems: MenuItem[] = [
        { label: 'Quick onboard driver & vehicle', icon: 'pi pi-sparkles', command: () => this.openQuickFleetOnboarding('owned') },
        { label: 'Add freelancer vehicle', icon: 'pi pi-plus-circle', command: () => this.addFreelancerVehicle() },
        { label: 'Onboard rental pair', icon: 'pi pi-key', command: () => this.openQuickFleetOnboarding('rental') },
        { label: 'Onboard transporter pair', icon: 'pi pi-building', command: () => this.openQuickFleetOnboarding('transporter') },
        { separator: true },
        { label: 'Bulk upload vehicles', icon: 'pi pi-upload', command: () => this.openBulkUpload() }
    ];
    private pendingLoads = 0;
    currentTab: 'owned' | 'freelancer' | 'rental' | 'transporter' = 'owned';
    activeTabIndex: 'owned' | 'freelancer' | 'rental' | 'transporter' = 'owned';

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    currentCompanyId: number | null = null;
    resourceView: 'current' | 'sister' = 'current';
    private sisterAccessErrorShown = false;
    sisterCompanyScope: SisterCompanyScope | null = null;
    private resourceScopeVersion = 0;
    selectedSisterCompanyId: number | null = null;

    ownedVehicleSource: Vehicle[] = [];
    ownedVehicles: Vehicle[] = [];
    ownedServerRecords = 0;
    totalOwnedRecords = 0;
    ownedFirst = 0;
    ownedRows = 10;

    freelancerVehicleSource: Vehicle[] = [];
    freelancerVehicles: Vehicle[] = [];
    freelancerFirst = 0;
    freelancerServerRecords = 0;
    totalFreelancerRecords = 0;
    freelancerRows = 10;

    rentalVehicleSource: Vehicle[] = [];
    rentalVehicles: Vehicle[] = [];
    rentalFirst = 0;
    rentalServerRecords = 0;
    totalRentalRecords = 0;
    rentalRows = 10;

    transporterOptions: TransporterFleetOption[] = [];
    selectedTransporterCrmClientId: number | null = null;
    transporterVehicleSource: Vehicle[] = [];
    transporterVehicles: Vehicle[] = [];
    transporterFirst = 0;
    transporterServerRecords = 0;
    totalTransporterRecords = 0;
    transporterRows = 10;

    vehicleSearchText = '';
    vehiclePlateSearch = '';
    vehicleRegistrationSearch = '';
    vehicleEngineSearch = '';
    vehicleTypeSearch = '';
    vehicleStatusSearch: boolean | null = null;
    readonly vehicleStatusOptions = [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];
    private vehicleStatusUpdating = new Set<number>();

    showAddFreelancerVehicleDialog = false;
    vehicleRegistrationNumber = '';
    searchVehicleInProgress = false;

    constructor(
        private vehicleService: VehicleService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private entityStatusService: EntityStatusService,
        private tenantScopeService: TenantScopeService,
        private crmService: CrmService
    ) { }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id ?? null;
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Vehicles' }];
        this.initializeSisterScope();
        this.route.queryParamMap.subscribe(queryParams => {
            const previousView = this.resourceView;
            const previousCompanyId = this.selectedSisterCompanyId;
            this.applyRouteState(queryParams);
            if (previousView !== this.resourceView || previousCompanyId !== this.selectedSisterCompanyId) {
                this.resetResourceScope(false);
                this.reloadAllVehicleLists();
            }
        });
    }

    get sisterCompanies(): Company[] {
        return this.sisterCompanyScope?.sisterCompanies ?? [];
    }

    get canUseSisterCompanyMode(): boolean {
        return !!this.sisterCompanyScope;
    }

    get isSisterMode(): boolean {
        return this.resourceView === 'sister' && this.canUseSisterCompanyMode;
    }

    get sisterCompanyFilterHint(): string {
        const groupName = this.sisterCompanyScope?.companyGroupName || 'the group';
        return `Default view shows all accessible sister companies in ${groupName}.`;
    }

    get sisterCompanyAllLabel(): string {
        return 'All sister companies';
    }

    get selectedTransporterName(): string {
        return this.transporterOptions.find(option => option.crmClientId === this.selectedTransporterCrmClientId)
            ?.transporterName || 'Transporter agency';
    }

    changeVehicleOwnership(vehicle: Vehicle): void {
        if (!this.currentCompanyId || this.isSisterMode || !vehicle?.vehicleId) return;
        this.ownershipTransferResources = [{ resourceType: 'VEHICLE', resourceId: vehicle.vehicleId, label: vehicle.plateNumber || `Vehicle #${vehicle.vehicleId}` }];
        const driver = vehicle.currentDriver;
        this.ownershipTransferPairedResource = driver?.id
            ? { resourceType: 'DRIVER', resourceId: driver.id, label: driver.name || `Driver #${driver.id}` }
            : null;
        this.ownershipTransferVisible = true;
    }

    onOwnershipTransferCompleted(): void {
        this.messageService.add({ severity: 'success', summary: 'Ownership updated', detail: 'Fleet ownership was corrected without recreating the resource.' });
        this.reloadAllVehicleLists();
    }

    editVehicle(vehicle: Vehicle): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/vehicle/edit', vehicle.vehicleId], {
            queryParams: this.buildVehicleRouteQueryParams(vehicle)
        });
    }

    viewVehicle(vehicle: Vehicle): void {
        this.router.navigate(['/fleet/vehicle/view', vehicle.vehicleId], {
            queryParams: this.buildVehicleRouteQueryParams(vehicle)
        });
    }

    deleteVehicle(vehicle: Vehicle): void {
        if (this.isSisterMode) {
            return;
        }
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this vehicle?',
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.vehicleService.deleteVehicle(vehicle).subscribe(response => {
                    if (response) {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Vehicle deleted successfully.' });
                        this.reloadCurrentTab();
                    } else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete vehicle.' });
                    }
                });
            }
        });
    }

    deactivateRentalVehicle(vehicle: Vehicle): void {
        if (this.isSisterMode) {
            return;
        }
        this.confirmationService.confirm({
            message: 'Are you sure you want to remove this rental vehicle from your company?',
            header: 'Confirm Rental Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (!this.currentCompanyId || !vehicle.vehicleId) {
                    return;
                }
                this.vehicleService.deactivateRentalVehicle(vehicle.vehicleId, this.currentCompanyId).subscribe(response => {
                    if (response.success) {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Rental vehicle removed successfully.' });
                        this.loadRentalVehicles();
                    } else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove rental vehicle.' });
                    }
                });
            }
        });
    }

    addOwnVehicle(): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/vehicle/create']);
    }

    openQuickFleetOnboarding(source: FleetResourceSource = 'owned'): void {
        if (this.isSisterMode) {
            return;
        }
        this.quickFleetOnboardingSource = source;
        this.quickFleetOnboardingVisible = true;
    }

    onQuickFleetOnboarded(): void {
        this.reloadAllVehicleLists();
    }

    openBulkUpload(): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/bulk-upload']);
    }

    addFreelancerVehicle(): void {
        if (this.isSisterMode) {
            return;
        }
        this.showAddFreelancerVehicleDialog = true;
    }

    addRentalVehicle(): void {
        this.openQuickFleetOnboarding('rental');
    }

    getPayloadTypesString(vehicle: Vehicle): string {
        return vehicle.payloadTypes.map(payloadType => payloadType.name).join(', ');
    }

    getTripTypesString(vehicle: Vehicle): string {
        return vehicle.tripTypes.map(tripType => tripType.name).join(', ');
    }

    payloadTypeNames(vehicle: Vehicle): string[] {
        return vehicle.payloadTypes?.map(payloadType => payloadType.name).filter((name): name is string => !!name) ?? [];
    }

    tripTypeNames(vehicle: Vehicle): string[] {
        return vehicle.tripTypes?.map(tripType => tripType.name).filter((name): name is string => !!name) ?? [];
    }

    loadOwnedData(event: TableLazyLoadEvent): void {
        this.ownedFirst = event.first ?? 0;
        this.ownedRows = event.rows ?? 10;
        this.loadOwnedVehicles();
    }

    loadFreelancerData(event: TableLazyLoadEvent): void {
        this.freelancerFirst = event.first ?? 0;
        this.freelancerRows = event.rows ?? 10;
        this.loadFreelancerVehicles();
    }

    loadRentalData(event: TableLazyLoadEvent): void {
        this.rentalFirst = event.first ?? 0;
        this.rentalRows = event.rows ?? 10;
        this.loadRentalVehicles();
    }

    loadTransporterData(event: TableLazyLoadEvent): void {
        this.transporterFirst = event.first ?? 0;
        this.transporterRows = event.rows ?? 10;
        this.loadTransporterVehicles();
    }

    onTransporterChange(transporterCrmClientId: number | null): void {
        this.selectedTransporterCrmClientId = transporterCrmClientId ? Number(transporterCrmClientId) : null;
        this.transporterFirst = 0;
        this.syncRouteState();
        this.loadTransporterVehicles();
    }

    tabChange(value: string | number): void {
        const resolved = this.resolveTabValue((value as string) || 'owned');
        this.activeTabIndex = resolved;
        this.currentTab = resolved;
        this.syncRouteState();
        this.reloadCurrentTab();
    }

    onResourceViewChange(value: 'current' | 'sister'): void {
        this.resourceView = value === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
        if (!this.isSisterMode) this.selectedSisterCompanyId = null;
        this.resetResourceScope();
        this.syncRouteState();
        this.reloadAllVehicleLists();
    }

    onSisterCompanyChange(companyId: number | null): void {
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(companyId) ? companyId : null;
        this.resetResourceScope();
        this.syncRouteState();
        this.reloadAllVehicleLists();
    }

    searchVehicle(): void {
        if (this.isSisterMode) {
            return;
        }
        this.searchVehicleInProgress = true;
        this.vehicleService.findVehicleByQuery('en-US', this.vehicleRegistrationNumber, undefined)
            .subscribe(vehicleWrapped => {
                if (vehicleWrapped.success && vehicleWrapped.data) {
                    this.addFreelanceVehicle(vehicleWrapped.data);
                } else {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Vehicle not found.' });
                    this.searchVehicleInProgress = false;
                    this.showAddFreelancerVehicleDialog = false;
                    this.router.navigate(['/fleet/vehicle/create'], {
                        queryParams: {
                            registrationNumber: this.vehicleRegistrationNumber,
                            isFreelancer: true
                        }
                    });
                }
            });
    }

    addFreelanceVehicle(vehicle: Vehicle): void {
        if (!this.currentCompanyId) {
            return;
        }
        this.vehicleService.addFreelancerVehicle(vehicle, this.currentCompanyId).subscribe(response => {
            if (response.success) {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Vehicle added as freelancer successfully.'
                });
                this.showAddFreelancerVehicleDialog = false;
                this.loadFreelancerVehicles();
            } else {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to add vehicle as freelancer.'
                });
            }
            this.searchVehicleInProgress = false;
        }, () => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to add vehicle as freelancer.'
            });
            this.searchVehicleInProgress = false;
        });
    }

    trackVehicle(vehicle: Vehicle): void {
        this.router.navigate(['/fleet/gps/vehicles', vehicle.vehicleId]);
    }

    hideBrokenImage(event: Event): void {
        (event.target as HTMLImageElement).style.display = 'none';
    }

    vehicleMetrics(): PageMetric[] {
        const labelPrefix = this.isSisterMode ? 'Sister ' : '';
        const hintPrefix = this.isSisterMode ? 'Other company ' : '';
        return [
            {
                label: `${labelPrefix}Owned Vehicles`,
                value: this.totalOwnedRecords,
                hint: `${hintPrefix}fleet records`,
                tone: 'info'
            },
            {
                label: `${labelPrefix}Freelancers`,
                value: this.totalFreelancerRecords,
                hint: `${hintPrefix}external vehicles`,
                tone: 'success'
            },
            {
                label: `${labelPrefix}Rental Vehicles`,
                value: this.totalRentalRecords,
                hint: `${hintPrefix}rented capacity links`,
                tone: 'warn'
            },
            {
                label: 'Transporter Vehicles',
                value: this.totalTransporterRecords,
                hint: 'External agency vehicles',
                tone: 'info'
            }
        ];
    }

    activeResultText(): string {
        const scopeLabel = this.isSisterMode ? 'sister-company ' : '';
        if (this.activeTabIndex === 'owned') {
            return `${this.totalOwnedRecords} ${scopeLabel}owned vehicles`;
        }
        if (this.activeTabIndex === 'rental') {
            return `${this.totalRentalRecords} ${scopeLabel}rental vehicles`;
        }
        if (this.activeTabIndex === 'transporter') {
            return `${this.totalTransporterRecords} transporter vehicles`;
        }
        return `${this.totalFreelancerRecords} ${scopeLabel}freelance vehicles`;
    }

    activeVehicleFilterCount(): number {
        return [
            this.vehicleSearchText,
            this.vehiclePlateSearch,
            this.vehicleRegistrationSearch,
            this.vehicleEngineSearch,
            this.vehicleTypeSearch,
            this.vehicleStatusSearch === null ? '' : 'status'
        ].filter(value => value?.toString().trim()).length;
    }

    clearVehicleFilters(): void {
        this.vehicleSearchText = '';
        this.vehiclePlateSearch = '';
        this.vehicleRegistrationSearch = '';
        this.vehicleEngineSearch = '';
        this.vehicleTypeSearch = '';
        this.vehicleStatusSearch = null;
        this.applyVehicleFilters();
    }

    updateVehicleStatus(vehicle: Vehicle, active: boolean): void {
        if (this.isSisterMode || !vehicle.vehicleId || vehicle.isActive === active) {
            return;
        }

        const previousStatus = vehicle.isActive !== false;
        vehicle.isActive = active;
        this.vehicleStatusUpdating.add(vehicle.vehicleId);

        this.vehicleService.updateVehicleActiveStatus(vehicle.vehicleId, active).subscribe({
            next: response => {
                if (response.success) {
                    this.patchVehicleEntityStatus(vehicle.vehicleId!, active);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `Vehicle marked ${active ? 'active' : 'inactive'}.`
                    });
                    this.applyVehicleFilters();
                } else {
                    vehicle.isActive = previousStatus;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: response.message || 'Failed to update vehicle status.'
                    });
                }
                this.vehicleStatusUpdating.delete(vehicle.vehicleId!);
            },
            error: () => {
                vehicle.isActive = previousStatus;
                this.vehicleStatusUpdating.delete(vehicle.vehicleId!);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update vehicle status.' });
            }
        });
    }

    isVehicleStatusUpdating(vehicle: Vehicle): boolean {
        return !!vehicle.vehicleId && this.vehicleStatusUpdating.has(vehicle.vehicleId);
    }

    vehicleStatusSeverity(vehicle: Vehicle): 'success' | 'danger' {
        return vehicle.isActive === false ? 'danger' : 'success';
    }

    applyVehicleFilters(): void {
        this.ownedVehicles = this.filterVehicles(this.ownedVehicleSource);
        this.freelancerVehicles = this.filterVehicles(this.freelancerVehicleSource);
        this.rentalVehicles = this.filterVehicles(this.rentalVehicleSource);
        this.transporterVehicles = this.filterVehicles(this.transporterVehicleSource);
        if (this.activeVehicleFilterCount() > 0) {
            this.totalOwnedRecords = this.ownedVehicles.length;
            this.totalFreelancerRecords = this.freelancerVehicles.length;
            this.totalRentalRecords = this.rentalVehicles.length;
            this.totalTransporterRecords = this.transporterVehicles.length;
        } else {
            this.totalOwnedRecords = this.ownedServerRecords;
            this.totalFreelancerRecords = this.freelancerServerRecords;
            this.totalRentalRecords = this.rentalServerRecords;
            this.totalTransporterRecords = this.transporterServerRecords;
        }
    }

    isRentalExpired(vehicle: Vehicle): boolean {
        return !!vehicle.rentalEndDate && new Date(vehicle.rentalEndDate).getTime() < new Date().setHours(0, 0, 0, 0);
    }

    private initializeSisterScope(): void {
        const applyScope = () => {
            this.sisterCompanyScope = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.vehicles.view']
            );
            this.applyRouteState(this.route.snapshot.queryParamMap);
            this.reloadAllVehicleLists();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private applyRouteState(queryParams: ParamMap): void {
        const tabValue = this.resolveTabValue(queryParams.get('tab'));
        this.activeTabIndex = tabValue;
        this.currentTab = tabValue;

        const requestedResourceView = queryParams.get('resourceView');
        this.resourceView = requestedResourceView === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';

        const requestedMemberCompanyId = this.toOptionalNumber(queryParams.get('memberCompanyId'));
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(requestedMemberCompanyId) ? requestedMemberCompanyId : null;
        this.selectedTransporterCrmClientId = this.toOptionalNumber(queryParams.get('transporterCrmClientId'));
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
        }
    }

    private resolveTabValue(tabKey: string | null): 'owned' | 'freelancer' | 'rental' | 'transporter' {
        if (!tabKey) {
            return 'owned';
        }
        return this.tabKeys.some(key => key === tabKey.toLowerCase())
            ? tabKey.toLowerCase() as 'owned' | 'freelancer' | 'rental' | 'transporter'
            : 'owned';
    }

    private syncRouteState(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                tab: this.currentTab,
                resourceView: this.resourceView,
                memberCompanyId: this.isSisterMode ? this.selectedSisterCompanyId : null,
                transporterCrmClientId: this.currentTab === 'transporter' ? this.selectedTransporterCrmClientId : null
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private loadOwnedVehicles(): void {
        const page = this.resolvePage(this.ownedFirst, this.ownedRows);
        const pageSize = this.ownedRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetOwnedVehicles();
                return;
            }
            this.bindVehicleRequest(
                this.vehicleService.findVehiclesByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.ownedVehicleSource = this.normalizeVehicleStatuses(response.items);
                    this.ownedServerRecords = response.count;
                    this.applyVehicleFilters();
                }
            );
            return;
        }

        const transportationAgencyId = this.sessionStorageService.getCompany()?.transportationAgencyId;
        if (!transportationAgencyId) {
            this.resetOwnedVehicles();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Transportation agency ID not found.' });
            return;
        }
        this.bindVehicleRequest(
            this.vehicleService.findVehiclesByTransportationAgencyId(page, pageSize, transportationAgencyId),
            response => {
                this.ownedVehicleSource = this.normalizeVehicleStatuses(response.items);
                this.ownedServerRecords = response.count;
                this.applyVehicleFilters();
            }
        );
    }

    private loadFreelancerVehicles(): void {
        const page = this.resolvePage(this.freelancerFirst, this.freelancerRows);
        const pageSize = this.freelancerRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetFreelancerVehicles();
                return;
            }
            this.bindVehicleRequest(
                this.vehicleService.getFreelanceVehiclesByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.freelancerVehicleSource = this.normalizeVehicleStatuses(response.items);
                    this.freelancerServerRecords = response.count;
                    this.applyVehicleFilters();
                }
            );
            return;
        }

        if (!this.currentCompanyId) {
            this.resetFreelancerVehicles();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            return;
        }
        this.bindVehicleRequest(
            this.vehicleService.getFreelanceVehiclesByCompanyId(page, pageSize, this.currentCompanyId),
            response => {
                this.freelancerVehicleSource = this.normalizeVehicleStatuses(response.items);
                this.freelancerServerRecords = response.count;
                this.applyVehicleFilters();
            }
        );
    }

    private loadRentalVehicles(): void {
        const page = this.resolvePage(this.rentalFirst, this.rentalRows);
        const pageSize = this.rentalRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetRentalVehicles();
                return;
            }
            this.bindVehicleRequest(
                this.vehicleService.getRentalVehiclesByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.rentalVehicleSource = this.normalizeVehicleStatuses(response.items);
                    this.rentalServerRecords = response.count;
                    this.applyVehicleFilters();
                }
            );
            return;
        }

        if (!this.currentCompanyId) {
            this.resetRentalVehicles();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            return;
        }
        this.bindVehicleRequest(
            this.vehicleService.getRentalVehiclesByCompanyId(page, pageSize, this.currentCompanyId),
            response => {
                this.rentalVehicleSource = this.normalizeVehicleStatuses(response.items);
                this.rentalServerRecords = response.count;
                this.applyVehicleFilters();
            }
        );
    }

    private loadTransporterOptions(): void {
        if (!this.currentCompanyId) {
            return;
        }
        const scopeVersion = this.resourceScopeVersion;
        this.crmService.getScopedTransporters(this.currentCompanyId).subscribe({
            next: options => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                this.transporterOptions = (options || []).filter(option => !this.isSisterMode
                    || isSisterCompanyResource(option.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId));
                if (this.selectedTransporterCrmClientId != null
                    && !this.transporterOptions.some(option => option.crmClientId === this.selectedTransporterCrmClientId)) {
                    this.selectedTransporterCrmClientId = null;
                }
                this.loadTransporterVehicles();
            },
            error: () => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                this.transporterOptions = [];
                this.resetTransporterVehicles();
                this.messageService.add({ severity: 'error', summary: 'Transporters unavailable', detail: 'Unable to load accessible transporter agencies.' });
            }
        });
    }

    private loadTransporterVehicles(): void {
        if (!this.currentCompanyId || !this.selectedTransporterCrmClientId) {
            this.resetTransporterVehicles();
            return;
        }
        const page = this.resolvePage(this.transporterFirst, this.transporterRows);
        const pageSize = this.transporterRows || 10;
        this.bindVehicleRequest(
            this.vehicleService.getTransporterVehicles(page, pageSize, this.currentCompanyId, this.selectedTransporterCrmClientId),
            response => {
                this.transporterVehicleSource = this.normalizeVehicleStatuses(response.items);
                this.transporterServerRecords = response.count;
                this.applyVehicleFilters();
            }
        );
    }

    private bindVehicleRequest(request: Observable<APIListWrapper<Vehicle>>, next: (response: APIListWrapper<Vehicle>) => void): void {
        const scopeVersion = this.resourceScopeVersion;
        this.beginPageLoad();
        request.pipe(finalize(() => this.endPageLoad())).subscribe({
            next: response => { if (scopeVersion === this.resourceScopeVersion) next(response); },
            error: error => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                if (error?.status === 403 && this.isSisterMode) {
                    if (!this.sisterAccessErrorShown) {
                        this.sisterAccessErrorShown = true;
                        this.resetOwnedVehicles();
                        this.resetFreelancerVehicles();
                        this.resetRentalVehicles();
                        this.messageService.add({ severity: 'error', summary: 'Sister-company vehicle access denied', detail: sisterCompanyFleetAccessMessage('vehicle') });
                    }
                    return;
                }
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load vehicles.' });
            }
        });
    }

    private resetResourceScope(clearTransporterSelection = true): void {
        this.resourceScopeVersion++;
        this.ownedFirst = this.freelancerFirst = this.rentalFirst = this.transporterFirst = 0;
        this.resetOwnedVehicles();
        this.resetFreelancerVehicles();
        this.resetRentalVehicles();
        this.resetTransporterVehicles();
        this.transporterOptions = [];
        if (clearTransporterSelection) this.selectedTransporterCrmClientId = null;
    }

    private reloadAllVehicleLists(): void {
        this.sisterAccessErrorShown = false;
        this.loadOwnedVehicles();
        this.loadFreelancerVehicles();
        this.loadRentalVehicles();
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
        }
    }

    private reloadCurrentTab(): void {
        this.sisterAccessErrorShown = false;
        if (this.currentTab === 'owned') {
            this.loadOwnedVehicles();
            return;
        }
        if (this.currentTab === 'rental') {
            this.loadRentalVehicles();
            return;
        }
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
            return;
        }
        this.loadFreelancerVehicles();
    }

    private filterVehicles(vehicles: Vehicle[]): Vehicle[] {
        const keyword = this.normalizeSearch(this.vehicleSearchText);
        const plate = this.normalizeSearch(this.vehiclePlateSearch);
        const registration = this.normalizeSearch(this.vehicleRegistrationSearch);
        const engine = this.normalizeSearch(this.vehicleEngineSearch);
        const type = this.normalizeSearch(this.vehicleTypeSearch);

        return vehicles.filter(vehicle => {
            const searchable = this.normalizeSearch([
                vehicle.plateNumber,
                vehicle.registrationNumber,
                vehicle.engineNumber,
                vehicle.type?.name,
                vehicle.config?.name,
                vehicle.capacity?.name,
                vehicle.make?.name,
                vehicle.model?.name,
                vehicle.year?.year,
                vehicle.resourceCompanyName,
                vehicle.payloadTypes?.map(payloadType => payloadType.name).join(' '),
                vehicle.tripTypes?.map(tripType => tripType.name).join(' '),
                vehicle.currentDriver?.name,
                vehicle.currentDriver?.phoneNumber,
                vehicle.rentalVendorName,
                vehicle.rentalContractReferenceNumber,
                vehicle.rentalRenewalStatus,
                vehicle.rentalStatus,
                vehicle.rentalNotes,
                vehicle.isActive === false ? 'inactive' : 'active'
            ].join(' '));

            return (!keyword || searchable.includes(keyword))
                && (!plate || this.normalizeSearch(vehicle.plateNumber).includes(plate))
                && (!registration || this.normalizeSearch(vehicle.registrationNumber).includes(registration))
                && (!engine || this.normalizeSearch(vehicle.engineNumber).includes(engine))
                && (!type || this.normalizeSearch(`${vehicle.type?.name || ''} ${vehicle.config?.name || ''} ${vehicle.capacity?.name || ''}`).includes(type))
                && (this.vehicleStatusSearch === null || (vehicle.isActive !== false) === this.vehicleStatusSearch);
        });
    }

    private normalizeSearch(value?: string | number): string {
        return (value || '').toString().trim().toLowerCase();
    }

    private normalizeVehicleStatuses(vehicles: Vehicle[] = []): Vehicle[] {
        return vehicles.map(vehicle => ({
            ...vehicle,
            isActive: vehicle.isActive !== false,
            tableRowKey: `${vehicle.vehicleId ?? 'vehicle'}-${vehicle.resourceCompanyId ?? 'current'}`
        }));
    }

    private patchVehicleEntityStatus(vehicleId: number, active: boolean): void {
        this.entityStatusService.setStatus('VEHICLE', vehicleId, active ? 'ACTIVE' : 'INACTIVE', 'Portal vehicle status update')
            .subscribe({
                error: () => {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Status History',
                        detail: 'Vehicle active flag was updated, but status history could not be saved.'
                    });
                }
            });
    }

    private buildVehicleRouteQueryParams(vehicle: Vehicle): Record<string, string | number> {
        const source = this.currentTab === 'freelancer'
            ? 'freelance'
            : this.currentTab === 'rental'
                ? 'rental'
                : this.currentTab === 'transporter'
                    ? 'transporter'
                    : 'owned';
        const queryParams: Record<string, string | number> = {
            source,
            tab: this.currentTab,
            resourceView: this.resourceView
        };

        if (this.isSisterMode && this.selectedSisterCompanyId != null) {
            queryParams['memberCompanyId'] = this.selectedSisterCompanyId;
        }
        if (vehicle.resourceCompanyId != null) {
            queryParams['resourceCompanyId'] = vehicle.resourceCompanyId;
        }
        if (vehicle.resourceCompanyName) {
            queryParams['resourceCompanyName'] = vehicle.resourceCompanyName;
        }
        if (source === 'transporter' && this.selectedTransporterCrmClientId != null) {
            queryParams['transporterCrmClientId'] = this.selectedTransporterCrmClientId;
        }

        if (source !== 'rental') {
            return queryParams;
        }

        if (vehicle.rentalVendorName) queryParams['vendorName'] = vehicle.rentalVendorName;
        if (vehicle.rentalVendorCompanyId != null) queryParams['vendorCompanyId'] = vehicle.rentalVendorCompanyId;
        if (vehicle.rentalContractReferenceNumber) queryParams['contractReferenceNumber'] = vehicle.rentalContractReferenceNumber;
        if (vehicle.rentalStartDate != null) queryParams['rentalStartDate'] = new Date(vehicle.rentalStartDate).getTime();
        if (vehicle.rentalEndDate != null) queryParams['rentalEndDate'] = new Date(vehicle.rentalEndDate).getTime();
        if (vehicle.rentalRateAmount != null) queryParams['rentalRateAmount'] = vehicle.rentalRateAmount;
        if (vehicle.rentalRateCurrency) queryParams['rentalRateCurrency'] = vehicle.rentalRateCurrency;
        if (vehicle.rentalRatePeriod) queryParams['rentalRatePeriod'] = vehicle.rentalRatePeriod;
        if (vehicle.rentalDepositAmount != null) queryParams['rentalDepositAmount'] = vehicle.rentalDepositAmount;
        if (vehicle.rentalDepositCurrency) queryParams['rentalDepositCurrency'] = vehicle.rentalDepositCurrency;
        if (vehicle.rentalRenewalStatus) queryParams['rentalRenewalStatus'] = vehicle.rentalRenewalStatus;
        if (vehicle.rentalBillId != null) queryParams['rentalBillId'] = vehicle.rentalBillId;
        if (vehicle.rentalExpenseId != null) queryParams['rentalExpenseId'] = vehicle.rentalExpenseId;
        if (vehicle.rentalNotes) queryParams['rentalNotes'] = vehicle.rentalNotes;

        return queryParams;
    }

    private resolvePage(first: number, rows: number): number {
        if (!rows) {
            return 0;
        }
        return Math.floor((first || 0) / rows);
    }

    private toOptionalNumber(value: string | null): number | null {
        if (!value) {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private isValidSisterCompanyId(companyId: number | null): boolean {
        return companyId != null && this.sisterCompanies.some(company => company.id === companyId);
    }

    private resetOwnedVehicles(): void {
        this.ownedVehicleSource = [];
        this.ownedVehicles = [];
        this.ownedServerRecords = 0;
        this.totalOwnedRecords = 0;
    }

    private resetFreelancerVehicles(): void {
        this.freelancerVehicleSource = [];
        this.freelancerVehicles = [];
        this.freelancerServerRecords = 0;
        this.totalFreelancerRecords = 0;
    }

    private resetRentalVehicles(): void {
        this.rentalVehicleSource = [];
        this.rentalVehicles = [];
        this.rentalServerRecords = 0;
        this.totalRentalRecords = 0;
    }

    private resetTransporterVehicles(): void {
        this.transporterVehicleSource = [];
        this.transporterVehicles = [];
        this.transporterServerRecords = 0;
        this.totalTransporterRecords = 0;
    }

    private beginPageLoad(): void {
        this.pendingLoads += 1;
        this.isLoading = true;
    }

    private endPageLoad(): void {
        this.pendingLoads = Math.max(0, this.pendingLoads - 1);
        this.isLoading = this.pendingLoads > 0;
    }
}
